import { type ReactNode } from "react";
import { IconCheck, IconPlus } from "@tabler/icons-react";
import { cn } from "../../lib/utils";

/**
 * What the row DOES, and therefore what it draws at its edges.
 *
 * A union rather than the `showIcon` boolean it replaces: the three renderings
 * differ in more than one glyph — the layout axis and the pressed state a screen
 * reader hears — and a boolean could only ever say one of those things.
 */
type ListItemAffordance =
  /** A plain action row, its content stacked. No selection is announced. */
  | { kind: "action" }
  /** Toggle with a trailing check-or-plus — the topic/chip rendering. */
  | { kind: "toggle" }
  /**
   * Toggle with no glyph at all (Figma "D7.pratiques", the desktop
   * `ListItem`): the moss surface is the whole answer, and `aria-pressed` is
   * what a screen reader hears.
   */
  | { kind: "select" }
  /**
   * Toggle with a LEADING checkbox (DS 5.0, Figma node 6686:6838). The
   * multi-select row: several answers are expected, and a box says so before it
   * is tapped where a check-or-plus says it only after.
   */
  | { kind: "checkbox" };

type ListItemProps = {
  /** Main content (text, or rich multi-line content). */
  children: ReactNode;
  /** Renders the selected state (moss surface, moss border, mint text). */
  selected: boolean;
  affordance: ListItemAffordance;
  /** `null` for a row that is not pressable (a rendered-only list). */
  onClick: (() => void) | null;
  disabled: boolean;
  /** Additional class names merged onto the root element. */
  className?: string;
};

function ListItem({
  children,
  selected,
  affordance,
  onClick,
  disabled,
  className,
}: ListItemProps): React.JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick === null ? undefined : onClick}
      // A toggle owes a screen reader its state. Without this, "checked" was
      // carried by a background colour and a tick glyph and by nothing a screen
      // reader says out loud. Action rows are plain actions and get no pressed
      // state to be wrong about.
      {...(affordance.kind === "action" ? {} : { "aria-pressed": selected })}
      className={cn(
        "w-full text-left shrink-0 cursor-pointer",
        "flex rounded-xl border border-solid",
        affordance.kind === "checkbox"
          ? "flex-row items-center gap-3 p-4"
          : affordance.kind === "select"
            ? null
            : "px-4 py-3",
        affordance.kind === "toggle" && "flex-row items-center gap-2",
        affordance.kind === "select" &&
          "min-h-14 flex-row items-center gap-2 px-4 py-2",
        affordance.kind === "action" &&
          "flex-col items-stretch justify-center gap-1",
        "font-dm-sans font-medium text-ui-body",
        "transition-colors duration-150 ease-out",
        "disabled:cursor-not-allowed disabled:opacity-50",
        selected
          ? "bg-ds-secondary border-ds-border-selected text-ds-primary"
          : cn(
              "border-ds-black/6",
              affordance.kind === "select"
                ? "text-ds-mint-900"
                : "text-ds-black",
              // Only the checkbox and select rows paint a surface: they sit on
              // the onboarding sheet in the design, while the older two are
              // used as chips over backgrounds that must show through.
              affordance.kind === "checkbox" || affordance.kind === "select"
                ? "bg-white"
                : "bg-transparent"
            ),
        className
      )}
    >
      {affordance.kind === "checkbox" && (
        <span
          aria-hidden
          className={cn(
            "flex size-5 shrink-0 items-center justify-center rounded-md",
            selected
              ? "bg-ds-primary text-ds-on-primary"
              : "border-2 border-solid border-ds-slate-400 bg-white"
          )}
        >
          {selected && <IconCheck size={12} stroke={3} />}
        </span>
      )}

      {affordance.kind === "action" ? (
        children
      ) : (
        <span className="flex min-w-0 flex-1 flex-col items-stretch justify-center gap-1">
          {children}
        </span>
      )}

      {affordance.kind === "toggle" &&
        (selected ? (
          <IconCheck
            size={16}
            stroke={1.5}
            className="shrink-0 text-ds-primary"
          />
        ) : (
          <IconPlus size={16} stroke={1.5} className="shrink-0 text-ds-black" />
        ))}
    </button>
  );
}

export { ListItem, type ListItemProps };
